"use client";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import {
  addToCartService,
  getCartService,
  updateAddOnQuantity,
  updateBaseProductQuantity,
} from "../../../api/cartApi/index.js";
import { resolveLatLongForServiceability } from "../helpers/savedAddressLatLong.js";
import {
  failedAddToCart,
  requestAddToCart,
  responseAddToCart,
  requestUpdateCart,
  requestUpdateBaseProductQuantity,
  responseUpdateCart,
  failedUpdateCart,
  responseGetCartItems,
  setCartItems,
} from "../../slices/cartSlice.js";
import { initiateFusCall } from "../commonSaga/index.js";
import { BUY_NOW, COUNTRIES } from "../../../constants/common/common.js";
import { resetPersonalizationValues, toggleClearPersonlizedData } from "../../slices/personalizationSlice/index.js";
import { removeLocalStorageItem } from "../../../utils/storage/localStorageUtil/index.js";
import {
  addAddonToCartPDPAction,
  addPdpOrderSeqId,
  pdpAddOnModalVisibility,
  resetPdpSelectedAddons,
  setAutoScroll,
} from "../../slices/pdpAddonSlice.js";
import isEmpty from "lodash.isempty";
import { setDeliveryDetailsAction } from "../../slices/common/shippingDateAndSlotSelection.js";
import { setSelectedDateFromAddToCart } from "../../slices/pdpSlice.js";
import { redirectToCheckout } from "../../../utils/common/index.js";
import { fetchCartAmountSummarySaga, fetchCompleteCartDetailsOnDeliveryPage } from "../deliveryAddressSaga/index.js";
import {
  setIsClearCartDueToCountryChange,
  setIsShowCartEmpty,
  setShouldRetryAddToCartAfterClear,
} from "../../slices/locationLock/index.js";

/**
 * Returns the first available date from the delivery-dates API response.
 *
 * @returns {Generator<*, string, *>} First non-blocked delivery date, or empty string.
 */
function* getFirstAvailableDeliveryDate() {
  const deliveryDates = yield select((state) => state?.shippingDateAndSlotSelection?.deliveryDates?.deliveryDates);
  const blockedDates = yield select((state) => state?.shippingDateAndSlotSelection?.blockedDates?.deliveryBlockDates);

  if (!Array.isArray(deliveryDates) || deliveryDates.length === 0) {
    return "";
  }

  const availableDates = deliveryDates.filter((date) => !blockedDates?.includes(date));
  return availableDates[0] || deliveryDates[0] || "";
}

/**
 *
 * @param {*} param
 */
export function* getCartSaga({ payload }) {
  try {
    const catalogId = yield select((state) => state.appConfig?.catalogId);
    const isDomestic = catalogId === COUNTRIES.INDIA;
    const latLong = yield call(resolveLatLongForServiceability);
    const response = yield call(getCartService, { payload, isDomestic, ...latLong });
    if (response?.data?.isSuccess) {
      yield put(setCartItems(response?.data?.orderList || []));
    }
  } catch (error) {
    yield put(failedUpdateCart(error));
  }
}

/**
 * Saga to handle adding an item to the cart, including API calls and state updates.
 * @param {object} payload - The payload containing data for the add-to-cart request.
 */
function* addToCartSaga({ payload }) {
  /**
   * Resets PDP delivery selection to the first date already present in the store
   * (from the prior delivery-dates response). Does not call delivery-dates again.
   * Also disables addons auto-scroll so a refreshed addons list does not scroll the page.
   *
   * @param {string} firstDeliveryDate - First available delivery date from store.
   */
  function* resetToFirstDeliveryDate(firstDeliveryDate) {
    yield put(setAutoScroll(false));
    yield put(setDeliveryDetailsAction(firstDeliveryDate ? { selectedDate: firstDeliveryDate } : {}));
    yield put(setSelectedDateFromAddToCart(firstDeliveryDate || ""));
  }

  /**
   * Handles post "Add to Cart" logic.
   *
   * If not a Buy Now action, resets delivery details to the first delivery-dates
   * API date. Otherwise, redirects to checkout or login.
   *
   * @param {Object} options
   * @param {string} options.isBuyNowClicked
   * @param {string} [options.firstDeliveryDate]
   */
  function* handleRedirection({ isBuyNowClicked, firstDeliveryDate }) {
    if (isBuyNowClicked !== BUY_NOW) {
      yield* resetToFirstDeliveryDate(firstDeliveryDate);
    } else {
      const action = yield call(redirectToCheckout);
      if (action) {
        yield put(action);
      }
    }
  }
  try {
    const isBuyNowClicked = yield select((state) => state.pdp?.checkBuyNowOrProceedToPay);
    const firstDeliveryDate = yield* getFirstAvailableDeliveryDate();

    const encodedPayload = new URLSearchParams(payload).toString();
    const isDomestic = payload?.countryGeoId === "IND";
    const response = yield call(addToCartService, encodedPayload, isDomestic);
    const latLong = yield call(resolveLatLongForServiceability);

    if (response) {
      if (response?.isDifferentLocation) {
        const cartItemsResponse = yield call(getCartService, { payload: "Y", isDomestic, ...latLong });
        if (cartItemsResponse?.data?.orderList?.length > 0) {
          yield put(setCartItems(cartItemsResponse?.data?.orderList));
          // Already on the target country page (manual URL / cookie city). Show ClearCart with
          // Change Location → clear cart, then retry ATC/Buy Now on this page.
          yield put(setIsClearCartDueToCountryChange(true));
          yield put(setShouldRetryAddToCartAfterClear(true));
          yield put(setIsShowCartEmpty(true));
        }
        yield call(initiateFusCall);
        return;
      }
      yield put(responseAddToCart(response));
      if (response?.errorMsg && !response?.isSuccess && response?.isProductStockAvailable === false) {
        yield call(initiateFusCall);
        return;
      }

      if (response?.isSuccess) {
        if (isBuyNowClicked !== BUY_NOW) {
          // Refetch cartItems so the PDP can tell this product is now in the cart
          // (drives "View Cart" + the in-cart "Customise now" footer) without a page refresh.
          const cartItemsResponse = yield call(getCartService, { payload: "Y", isDomestic, ...latLong });
          if (cartItemsResponse?.data?.orderList?.length >= 0) {
            yield put(setCartItems(cartItemsResponse?.data?.orderList || []));
          }
        }
      }

      yield call(initiateFusCall);
      yield put(addPdpOrderSeqId(response?.orderItemSeqId));
      const latestPdpAddons = yield select((state) => state?.pdpAddon);
      if (isEmpty(latestPdpAddons?.addOnInfo?.addonsList?.[0]?.products)) {
        yield handleRedirection({ isBuyNowClicked, firstDeliveryDate });
      } else {
        if (latestPdpAddons?.selectedAddons?.length === 0) {
          yield put(pdpAddOnModalVisibility(true));
        } else {
          const formData = new FormData();
          const addonsPayload = {
            countryGeoId: payload?.countryGeoId === "IND" ? "" : payload?.countryGeoId,
            itemDesiredDeliveryDate: payload?.itemDesiredDeliveryDate,
            masterProductId: payload?.add_product_id,
            orderItemSeqId: response?.orderItemSeqId,
            pinCode: payload?.matchedPincode,
            ...(payload?.countryGeoId !== "IND" && {
              shippingMethodId: payload?.shippingMethodId,
              timeSlotId: payload?.timeSlotId,
            }),
          };

          for (const key in addonsPayload) {
            formData.append(key, addonsPayload[key]);
          }

          latestPdpAddons?.selectedAddons?.forEach((item) => {
            const itemP = `${item.productId}_${item.quantity}`;
            formData.append("addonProductId", item.isFromCart ? `${itemP}_${item.itemIndex}` : itemP);
          });

          const queryString = new URLSearchParams(formData).toString();
          yield put(addAddonToCartPDPAction({ param: queryString }));
          yield handleRedirection({ isBuyNowClicked, firstDeliveryDate });
          yield put(resetPdpSelectedAddons());
        }
      }
      yield put(resetPersonalizationValues());
      removeLocalStorageItem(payload?.add_product_id);
      yield delay(0);
      yield put(toggleClearPersonlizedData());
    } else {
      yield put(failedAddToCart(response?.data));
    }
  } catch (error) {
    yield put(failedAddToCart(error));
  }
}

/**
 * Saga to update add-on quantity on the checkout page and refresh delivery-address cart data.
 * @param {object} action - The action containing payload data.
 */
function* updateCartSaga({ payload }) {
  try {
    const encodedPayload = new URLSearchParams(payload).toString();
    const response = yield updateAddOnQuantity(encodedPayload);
    yield put(responseUpdateCart(response));
    yield call(fetchCompleteCartDetailsOnDeliveryPage);
    yield call(fetchCartAmountSummarySaga);
  } catch (error) {
    yield put(failedUpdateCart(error));
  }
}

/**
 * Saga to update base product quantity on delivery address checkout.
 * @param {object} action - The action containing URL-encoded form payload.
 */
function* updateBaseProductQuantitySaga({ payload }) {
  try {
    const response = yield call(updateBaseProductQuantity, payload);
    if (response?.isSuccess) {
      yield put(responseUpdateCart(response));
      yield call(fetchCompleteCartDetailsOnDeliveryPage);
      yield call(fetchCartAmountSummarySaga);
      yield call(initiateFusCall);
    } else {
      yield put(failedUpdateCart(response));
    }
  } catch (error) {
    yield put(failedUpdateCart(error));
  }
}

/**
 * Watcher saga for cart actions.
 */
export function* watcherForCartSaga() {
  yield takeLatest(requestUpdateCart.type, updateCartSaga);
  yield takeLatest(requestUpdateBaseProductQuantity.type, updateBaseProductQuantitySaga);
  yield takeLatest(requestAddToCart.type, addToCartSaga);
  yield takeLatest(responseGetCartItems.type, getCartSaga);
}
